/* eslint-disable react/jsx-filename-extension */
/* eslint-disable import/no-named-as-default */
import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import set from 'lodash/set';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router';
import useScreenSize from '@app/components/Hooks/use-screen-size';
import { useCheckPermissions } from '@app/helpers/common';
import { clearNotification, setCustomParamsViaCookie } from '../actions/ui';
import { showNotification, checkedBrowser } from '../actions/notification';

import MyInsights from './MyInsights/MyInsights';
import Claims from './Claims/claims';
import Policies from './Policies/policies';
import Cases from './Cases/Cases';
import Settings from './Settings/settings';
import ManagementRoot from './Management/management-root';
import Chats from './Chats/chats';
import AnalyticsRoot from './Analytics/analytics-root';
import MassNotificationsRoot from './MassNotifications/mass-notifications-root';
import ErrorModal from './ErrorModal/error-modal';
import { getBrowserCheckCookie } from '../helpers/cookies';
import {
  ANALYTICS_TAB,
  CLAIMS_TAB,
  POLICY_TAB,
  USER_MANAGEMENT_TAB,
  CASES_TAB,
  CHATS_TAB,
  MASSNOTIFICATIONS_TAB,
  SYSTEM_ADMIN,
  SHOW_MY_INSIGHTS,
} from '../constants/permissions';

// Don't remove this line it has all the styling for the dashboard modal
import './ModalPage/ModalPage.less';
import usePermissionVerify from './elements/hooks/usePermissionVerify';
import ProfileConfig from './ProfileConfig/profile-config';
import secureRequest from '../helpers/secure-request';

export const URL_PANEL_HASH = {
  home: { Panel: MyInsights, neededPermissions: [] },
  insights: { Panel: AnalyticsRoot, neededPermissions: [ANALYTICS_TAB] },
  analytics: { Panel: AnalyticsRoot, neededPermissions: [ANALYTICS_TAB] },
  cases: { Panel: Cases, neededPermissions: [CASES_TAB] },
  chats: { Panel: Chats, neededPermissions: [CHATS_TAB] },
  claims: { Panel: Claims, neededPermissions: [CLAIMS_TAB] },
  massnotification: {
    Panel: MassNotificationsRoot,
    neededPermissions: [MASSNOTIFICATIONS_TAB],
  },
  policies: { Panel: Policies, neededPermissions: [POLICY_TAB] },
  usermgmt: { Panel: ManagementRoot, neededPermissions: [USER_MANAGEMENT_TAB] },
  manage: { Panel: ManagementRoot, neededPermissions: [USER_MANAGEMENT_TAB] },
  settings: { Panel: Settings, neededPermissions: [SYSTEM_ADMIN] },
  profile: { Panel: ProfileConfig },
};

const processQueryString = (queryString) => {
  const query = {};
  if (queryString.length > 0) {
    queryString.split('&').forEach((s) => {
      const split = s.split('=');
      set(query, split[0], split[1]);
    });
  }
  return query;
};

const Dashboard = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  let { panelId } = params;
  const { subNavId, id } = params;

  const isChrome = !!window.chrome; // eslint-disable-line
  const {
    checkedBrowser,
    setCustomParamsViaCookie,
    showNotification,
    ui,
    clearNotification,
  } = props;
  const { currentPermissions, showProfileConfig } = useSelector(
    (state) => ({
      currentPermissions: get(state, 'auth.permissions', []),
      showProfileConfig: state.ui?.showProfileConfig,
    }),
  );
  const { audioNotification, muted } = ui || {};
  const [showBrowserCheckModal, setShowBrowserCheckModal] = useState(false);
  const [caseDataByStockNumber, setCaseDataByStockNumber] = useState(null);
  const isSmallScreen = useScreenSize();

  useEffect(() => {
    setCustomParamsViaCookie();
    if (!getBrowserCheckCookie() && !isChrome) setShowBrowserCheckModal(true);
    if (!isChrome) {
      showNotification({
        text: 'This browser is not supported. Please use Chrome.',
        persistent: true,
        canClose: false,
      });
    }
  }, [isChrome, setCustomParamsViaCookie, showNotification]);

  useEffect(() => {
    if (audioNotification) {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      setTimeout(clearNotification, 2000);
    }
  }, [audioNotification, clearNotification]);

  const isMyInsightsEnabled = useCheckPermissions([SHOW_MY_INSIGHTS]);
  if (!panelId) {
    if (isSmallScreen) {
      navigate('/chats');
    } else if (isMyInsightsEnabled) {
      panelId = 'home';
    } else if (currentPermissions.includes(CHATS_TAB)) {
      navigate('/chats');
    } else {
      navigate('/analytics');
    }
  } else if (!['chats', 'profile', 'create'].includes(panelId) && isSmallScreen) {
    navigate('/chats');
  }

  if (caseDataByStockNumber && panelId === 'copart-stock-number') {
    navigate(`/chats/${caseDataByStockNumber._id}`);
  }

  useEffect(() => {
    if (panelId === 'copart-stock-number' && subNavId) {
      const searchParams = new URLSearchParams({
        totalLossStockNumber: subNavId,
      });
      secureRequest(`/api/job/jobList?${searchParams.toString()}`, 'GET').then((res) => res.json())
        .then(({ jobs }) => {
          const [caseData] = jobs;
          setCaseDataByStockNumber(caseData ?? {});
        });
    }
  }, [panelId, subNavId]);
  const { Panel, neededPermissions } = get(URL_PANEL_HASH, showProfileConfig ? 'profile' : panelId, {});
  const { hasAccess } = usePermissionVerify({ neededPermissions });
  const hasPermissions = currentPermissions.length > 0;

  const query = processQueryString(get(props, 'location.search', '').slice(1));

  return (
    !!Panel && (
      <main id="main" className="dashboard">
        {audioNotification && !muted && (
          <audio autoPlay>
            <source src="/sounds/notify.wav" />
            <track kind="captions" />
          </audio>
        )}
        {hasPermissions && hasAccess && (
          <Panel
            data-test="main-component"
            tab={query.tab}
            subNavId={subNavId}
            id={id}
          />
        )}
        {hasPermissions && !hasAccess && navigate('/')}
        {showBrowserCheckModal && (
          <ErrorModal
            checkedBrowser={checkedBrowser}
            show
            title="Your Browser Is Not Supported"
          >
            Please use
            {' '}
            <a href="https://www.google.com/chrome/">Chrome</a>
            .
          </ErrorModal>
        )}
      </main>
    )
  );
};

Dashboard.propTypes = {
  checkedBrowser: PropTypes.func.isRequired,
  clearNotification: PropTypes.func.isRequired,
  setCustomParamsViaCookie: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  ui: PropTypes.instanceOf(Object).isRequired,
};

export { Dashboard };

const mapStateToProps = (state) => ({
  ui: state.ui,
  currentPermissions: get(state, 'auth.permissions', []),
});

const mapDispatchToProps = {
  clearNotification,
  checkedBrowser,
  showNotification,
  setCustomParamsViaCookie,
};

export const DashboardWithoutAuth = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Dashboard);

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
